import { supabase } from "../lib/supabase";
import { paymentService } from "./paymentService";
import { getCurrentBranding } from "../utils/branding";
import { authService } from "./authService";
import { interpretVerifyResponse } from "../utils/razorpayVerification";
import { getCurrentPlatform, getCurrentCurrency } from "../utils/platform";
import {
  getPaymentContactValue,
  validatePaymentContact,
} from "../utils/paymentContact";
import {
  buildAccreditationTypes,
  buildCheckoutSourceMetadata,
  buildPaymentDiscountPayload,
  buildRefresherInfo,
  getPaymentPartnerId,
} from "../utils/paymentPayloads";

// supabase-js hides non-2xx bodies behind FunctionsHttpError.context (a
// Response). Read it once so callers can act on structured error payloads.
const readEdgeFunctionErrorBody = async (error) => {
  try {
    return (await error?.context?.json?.()) || null;
  } catch {
    return null;
  }
};

const RAZORPAY_CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
let razorpayScriptPromise = null;
let shouldReportRazorpayScriptError = false;

class RazorpayService {
  constructor() {
    this.razorpayKey = import.meta.env.VITE_RAZORPAY_KEY_ID;
    this.processingPayments = new Set(); // Track payments being processed
    this.verifyRetryDelaysMs = [3000, 6000]; // re-verify while capture is pending
  }

  async createOrder(orderData) {
    try {
      // Ensure the Supabase client has a fresh token before any DB write —
      // stale JWTs cause auth.uid() to be null and RLS rejects the payments insert.
      await authService.ensureFreshToken();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("Authentication required");
      const userId = orderData?.user_id || session.user.id;
      if (session.user.id !== userId) {
        throw new Error("User authentication mismatch");
      }
      const amount = orderData?.amount ?? orderData?.totalAmount;
      const courseIds = orderData?.course_ids || orderData?.courseIds || [];
      const bundleIds = orderData?.bundle_ids || orderData?.bundleIds || [];
      const partnerId = getPaymentPartnerId(orderData);
      const accreditationTypes = buildAccreditationTypes(orderData);
      const refresherInfo = buildRefresherInfo(orderData);
      const discountPayload = buildPaymentDiscountPayload(orderData);
      const checkoutSourceMetadata = buildCheckoutSourceMetadata({
        url: orderData?.sourceUrl || orderData?.source_url,
        partnerId,
      });
      const isCartPurchase =
        orderData?.is_cart_purchase ||
        orderData?.isCartPurchase ||
        courseIds.length > 0 ||
        bundleIds.length > 0;
      const siteUrl =
        orderData?.siteUrl ||
        orderData?.site_url ||
        (typeof window !== "undefined" ? window.location.origin : undefined);

      // Create order in database first (keep track locally)
      const paymentRecord = await paymentService?.createPayment({
        user_id: userId,
        course_id: orderData?.course_id,
        enrollment_id: orderData?.enrollment_id,
        amount,
        currency: orderData?.currency || "INR",
        payment_status: "pending",
        ...(isCartPurchase
          ? {
              payment_gateway_response: {
                is_cart_purchase: true,
                course_ids: JSON.stringify(courseIds),
                bundle_ids: JSON.stringify(bundleIds),
                cart_item_count: orderData?.cart_item_count || courseIds.length,
                ...(partnerId ? { partner_id: partnerId } : {}),
                ...checkoutSourceMetadata,
                ...(Object.keys(accreditationTypes).length > 0
                  ? { accreditation_types: JSON.stringify(accreditationTypes) }
                  : {}),
                ...(Object.keys(refresherInfo).length > 0
                  ? { refresher_info: JSON.stringify(refresherInfo) }
                  : {}),
              },
            }
          : {}),
      });

      // Call Edge Function to create real Razorpay order
      const { data: orderResponse, error: orderError } =
        await supabase.functions.invoke("create-payment-order", {
          body: {
            amount: Math.round(parseFloat(amount) * 100), // Send in paise
            paymentId: paymentRecord?.id,
            currency: orderData?.currency || "INR",
            courseId: orderData?.course_id,
            courseIds, // For cart purchases
            bundleIds, // For bundle purchases
            isCartPurchase,
            // Shorten receipt ID to comply with Razorpay 40 char limit
            receipt: `rcpt_${paymentRecord?.id.slice(-12)}`,
            platform: getCurrentPlatform(),
            teamId: partnerId || undefined,
            ...discountPayload,
            accreditationTypes,
            refresherInfo,
            siteUrl,
            checkoutSourceMetadata,
          },
        });

      if (orderError) {
        const body = await readEdgeFunctionErrorBody(orderError);
        const orderFailure = new Error(
          body?.error ||
            orderError?.message ||
            "Failed to create Razorpay order",
        );
        // The 409 already-owned response names the offending courses so the
        // cart can say which line (or bundle) to remove.
        if (Array.isArray(body?.owned_course_ids)) {
          orderFailure.ownedCourseIds = body.owned_course_ids;
        }
        if (Array.isArray(body?.overlapping_course_ids)) {
          orderFailure.overlappingCourseIds = body.overlapping_course_ids;
        }
        throw orderFailure;
      }

      const razorpayOrder = orderResponse?.order;

      return {
        ...razorpayOrder,
        payment_id: paymentRecord?.id,
        paymentId: paymentRecord?.id,
      };
    } catch (error) {
      console.error("Razorpay order creation error:", error);
      const wrapped = new Error(
        error?.message || "Failed to create payment order",
      );
      if (error?.ownedCourseIds) wrapped.ownedCourseIds = error.ownedCourseIds;
      if (error?.overlappingCourseIds) {
        wrapped.overlappingCourseIds = error.overlappingCourseIds;
      }
      throw wrapped;
    }
  }

  async openCheckout(orderDetails, options = {}) {
    return new Promise((resolve, reject) => {
      if (!window?.Razorpay) {
        reject(new Error("Razorpay SDK not loaded"));
        return;
      }

      const branding = getCurrentBranding();
      const contact = getPaymentContactValue(options?.prefill);
      const contactError = contact
        ? validatePaymentContact(contact, {
            currency: orderDetails?.currency || "INR",
          })
        : null;

      if (contactError) {
        reject(new Error(contactError));
        return;
      }

      // Razorpay lets the learner retry inside the modal after a failed
      // attempt, so a failure is not final: settle only on success or when
      // the modal closes (reporting the last failure, if any).
      let lastFailure = null;
      const razorpayOptions = {
        key: this.razorpayKey,
        amount: orderDetails?.amount,
        currency: orderDetails?.currency || "INR",
        name: options?.name || branding.name,
        description: options?.description || "Course Enrollment Payment",
        order_id: orderDetails?.id, // Use the real order ID from backend
        image: options?.logo || branding.logoFull,
        handler: (response) => {
          resolve({
            success: true,
            razorpay_payment_id: response?.razorpay_payment_id,
            razorpay_order_id: response?.razorpay_order_id,
            razorpay_signature: response?.razorpay_signature,
          });
        },
        prefill: {
          name: options?.prefill?.name || "",
          email: options?.prefill?.email || "",
          contact,
        },
        readonly: contact ? { contact: true } : undefined,
        theme: {
          color: options?.theme?.color || "#3B82F6",
        },
        modal: {
          ondismiss: () => {
            if (lastFailure) {
              const description =
                lastFailure?.error?.description ||
                "Payment failed. Please try again.";
              const failure = new Error(description);
              failure.code = lastFailure?.error?.code;
              failure.description = description;
              reject(failure);
              return;
            }
            reject(new Error("Payment cancelled by user"));
          },
        },
        retry: {
          enabled: true,
          max_count: 3,
        },
        timeout: 300, // 5 minutes
        remember_customer: true,
        notes: options?.notes || {
          course_id: options?.metadata?.course_id || "",
          enrollment_id: options?.metadata?.enrollment_id || "",
          user_id: options?.metadata?.user_id || "",
        },
      };

      const razorpay = new window.Razorpay(razorpayOptions);

      if (razorpay && typeof razorpay.on === "function") {
        razorpay.on("payment.failed", (response) => {
          console.error("Razorpay payment failed:", response);
          lastFailure = response;
        });
      }

      if (razorpay && typeof razorpay.open === "function") {
        razorpay.open();
      }
    });
  }

  // `retry: false` makes exactly one verify-payment call: the cart's
  // "Check payment status" action. verify-payment is idempotent for an
  // order, and this never opens checkout, so a check cannot charge again.
  async verifyPayment(paymentData, { retry = true } = {}) {
    try {
      const { razorpay_payment_id, razorpay_order_id, razorpay_signature } =
        paymentData;

      if (!razorpay_payment_id || !razorpay_order_id) {
        throw new Error("Invalid payment data");
      }

      if (this.processingPayments.has(razorpay_payment_id)) {
        throw new Error("Payment is already being processed");
      }
      this.processingPayments.add(razorpay_payment_id);

      // Get a valid session with a fresh access token
      // Ensure fresh token for verify call
      await authService.ensureFreshToken();

      // An authorized payment is not fulfilled until Razorpay captures it.
      // Re-verify a few times; if it is still pending, the learner must not
      // pay again (a later check of the same order can still complete it).
      let outcome = { state: "pending" };
      const attempts = retry ? this.verifyRetryDelaysMs.length : 0;
      for (let attempt = 0; attempt <= attempts; attempt++) {
        if (attempt > 0) {
          await new Promise((resolve) =>
            setTimeout(resolve, this.verifyRetryDelaysMs[attempt - 1]),
          );
        }
        const { data: verificationResult, error: verifyError } =
          await supabase.functions.invoke("verify-payment", {
            body: {
              razorpay_order_id,
              razorpay_payment_id,
              razorpay_signature,
            },
          });
        const status = verifyError
          ? verifyError?.context?.status ?? 500
          : 200;
        const body = verifyError
          ? await verifyError?.context?.json?.().catch(() => null)
          : null;
        outcome = interpretVerifyResponse({
          data: verificationResult,
          status,
          body,
        });
        if (outcome.state !== "pending") break;
      }

      if (outcome.state === "not_paid") {
        const notPaid = new Error(
          outcome.message || "Payment has not been completed",
        );
        notPaid.notPaid = true;
        throw notPaid;
      }
      // Captured but held for manual review: not a bank delay, and no
      // re-verification will grant access by itself.
      if (outcome.state === "review") {
        return {
          success: false,
          pending: true,
          underReview: true,
          razorpay_payment_id,
          razorpay_order_id,
        };
      }
      if (outcome.state === "pending") {
        return {
          success: false,
          pending: true,
          razorpay_payment_id,
          razorpay_order_id,
        };
      }

      // Update local payment status (optional, as backend handles it, but good for UI consistency)
      // We can fetch the updated payment record to be sure
      const { data: updatedPayment } = await supabase
        .from("payments")
        .select("*")
        .eq("razorpay_order_id", razorpay_order_id)
        .single();

      return {
        success: true,
        payment: updatedPayment,
        verified: true,
        razorpay_payment_id,
        razorpay_order_id,
        razorpay_signature,
      };
    } catch (error) {
      if (paymentData?.razorpay_payment_id) {
        this.processingPayments.delete(paymentData.razorpay_payment_id);
      }
      console.error("Payment verification error:", error);
      const failure = new Error(
        error?.message || "Payment verification failed",
      );
      if (error?.notPaid) failure.notPaid = true;
      throw failure;
    } finally {
      if (paymentData?.razorpay_payment_id) {
        this.processingPayments.delete(paymentData.razorpay_payment_id);
      }
    }
  }

  async handlePaymentFailure(orderData, errorDetails) {
    return {
      success: false,
      error: errorDetails,
      retry_available: true,
      razorpay_order_id: orderData?.razorpay_order_id,
    };
  }

  loadRazorpayScript({ reportError = true } = {}) {
    if (reportError) shouldReportRazorpayScriptError = true;

    if (typeof window === "undefined" || typeof document === "undefined") {
      return Promise.resolve(false);
    }

    if (window.Razorpay) {
      return Promise.resolve(true);
    }

    if (razorpayScriptPromise) {
      return razorpayScriptPromise;
    }

    razorpayScriptPromise = new Promise((resolve) => {
      const script =
        document.querySelector(`script[src="${RAZORPAY_CHECKOUT_SRC}"]`) ||
        document.createElement("script");
      const isNewScript = !script.parentElement;

      script.src = RAZORPAY_CHECKOUT_SRC;
      script.async = true;
      script.onload = () => {
        shouldReportRazorpayScriptError = false;
        resolve(true);
      };
      script.onerror = (error) => {
        razorpayScriptPromise = null;
        if (shouldReportRazorpayScriptError) {
          console.error("Failed to load Razorpay script:", error);
        }
        resolve(false);
      };

      if (isNewScript) {
        document.body.appendChild(script);
      }
    });

    return razorpayScriptPromise;
  }

  async createPaymentLink(paymentData) {
    try {
      // Ensure fresh token and verify session BEFORE inserting —
      // a stale JWT makes auth.uid() null and the payments RLS check fails.
      await authService.ensureFreshToken();
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        throw new Error("Please sign in to create payment link");
      }

      // Verify the user ID matches
      if (session.user.id !== paymentData.user_id) {
        console.error("User ID mismatch:", {
          sessionUserId: session.user.id,
          paymentDataUserId: paymentData.user_id,
        });
        throw new Error("User authentication mismatch");
      }

      // Create a pending payment record
      const paymentRecord = await paymentService?.createPayment({
        user_id: paymentData?.user_id,
        course_id: paymentData?.course_id,
        enrollment_id: paymentData?.enrollment_id,
        amount: paymentData?.amount,
        currency: paymentData?.currency || "INR",
        payment_status: "pending",
      });

      // Call the Supabase Edge Function to create a payment link
      const { data: paymentLinkData, error: linkError } =
        await supabase.functions.invoke("create-payment-link", {
          body: {
            paymentData: {
              amount: paymentData?.amount,
              currency: paymentData?.currency || "INR",
              description:
                paymentData?.description ||
                `Enrollment for course ${paymentData?.course_id}`,
              course_id: paymentData?.course_id,
              enrollment_id: paymentData?.enrollment_id,
              customer_name: paymentData?.customer_name || "",
              customer_email: paymentData?.customer_email || "",
              customer_phone: paymentData?.customer_phone || "",
              platform: getCurrentPlatform(),
              payment_id: paymentRecord?.id,
            },
          },
        });

      if (linkError) {
        console.error("Edge Function error:", linkError);
        throw new Error(linkError?.message || "Failed to create payment link");
      }

      return {
        ...paymentLinkData,
        payment_id: paymentRecord?.id,
        database_payment: paymentRecord,
      };
    } catch (error) {
      console.error("Razorpay payment link creation error:", error);
      throw new Error(error?.message || "Failed to create payment link");
    }
  }

  async handlePaymentLinkRedirect(paymentLinkId, paymentId, razorpaySignature) {
    try {
      if (!paymentLinkId) {
        console.error("Invalid payment data - missing payment link ID");
        throw new Error("Invalid payment data - missing payment link ID");
      }

      // Check if payment is already being processed
      if (paymentId && this.processingPayments.has(paymentId)) {
        throw new Error("Payment is already being processed");
      }

      // Add to processing set if we have paymentId
      if (paymentId) {
        this.processingPayments.add(paymentId);
      }

      // FIXED: Use JSON contains query directly (avoid the problematic filter syntax)
      let { data: paymentRecords, error: fetchError } = await supabase
        .from("payments")
        .select("*")
        .contains("payment_gateway_response", {
          payment_link_id: paymentLinkId,
        });

      // If not found, try searching by other criteria as fallback
      if ((!paymentRecords || paymentRecords.length === 0) && paymentId) {
        // Get recent pending payments as fallback
        ({ data: paymentRecords, error: fetchError } = await supabase
          .from("payments")
          .select("*")
          .eq("payment_status", "pending")
          .order("created_at", { ascending: false })
          .limit(5)); // Get multiple to find the right one
      }

      if (fetchError) {
        console.error("Error fetching payment record:", fetchError);
        throw new Error("Failed to retrieve payment information");
      }

      if (!paymentRecords || paymentRecords.length === 0) {
        console.error(
          "Payment record not found for payment link:",
          paymentLinkId,
        );
        throw new Error("Payment record not found");
      }

      const paymentRecord = paymentRecords[0];
      if (paymentRecord.payment_status !== "completed") {
        throw new Error(
          "Payment was received and is still being verified. Please refresh your courses shortly.",
        );
      }

      return {
        success: true,
        payment: paymentRecord,
        verified: true,
        paymentId,
        paymentLinkId,
        razorpaySignature,
      };
    } catch (error) {
      // Remove from processing set on error
      if (paymentId) {
        this.processingPayments.delete(paymentId);
      }
      console.error("Payment link verification error:", error);
      console.error("Stack trace:", error.stack);
      throw new Error(error?.message || "Payment verification failed");
    } finally {
      // Always remove from processing set when done
      if (paymentId) {
        this.processingPayments.delete(paymentId);
      }
    }
  }

  // EMI options for premium courses
  getEMIOptions(amount) {
    if (amount < 10000) return [];

    return [
      { duration: 3, monthly_amount: Math?.ceil(amount / 3), interest_rate: 0 },
      { duration: 6, monthly_amount: Math?.ceil(amount / 6), interest_rate: 2 },
      {
        duration: 12,
        monthly_amount: Math?.ceil(amount / 12),
        interest_rate: 5,
      },
    ];
  }

  // Corporate payment options
  async createCorporateOrder(orderData) {
    try {
      const corporateOrder = await this.createOrder({
        ...orderData,
        payment_method: "corporate",
        currency: getCurrentCurrency(),
      });

      return {
        ...corporateOrder,
        corporate_features: {
          bulk_enrollment: true,
          invoice_generation: true,
          gst_compliance: true,
          purchase_order_support: true,
        },
      };
    } catch (error) {
      throw new Error(error?.message || "Failed to create corporate order");
    }
  }
}

export const razorpayService = new RazorpayService();
